import { useState, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'

export interface StringListInputProps {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  id?: string
  ariaLabel?: string
}

/** A list of short strings as chips: type, Enter (or comma) adds; Backspace on empty removes the last. */
export function StringListInput({
  value,
  onChange,
  placeholder = 'Add and press Enter',
  disabled,
  id,
  ariaLabel,
}: StringListInputProps) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const v = draft.trim().replace(/,$/, '')
    if (!v) return
    if (!value.includes(v)) onChange([...value, v])
    setDraft('')
  }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      add()
    } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1))
  }
  return (
    <div className={`input flex min-h-[32px] flex-wrap items-center gap-1 ${disabled ? 'opacity-60' : ''}`}>
      {value.map((v) => (
        <span key={v} className="badge badge-gray">
          {v}
          {!disabled && (
            <button
              type="button"
              aria-label={`Remove ${v}`}
              onClick={() => onChange(value.filter((x) => x !== v))}
              className="rounded hover:text-red-600"
            >
              <X size={10} />
            </button>
          )}
        </span>
      ))}
      <input
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={add}
        placeholder={value.length ? '' : placeholder}
        className="min-w-[80px] flex-1 bg-transparent text-xs outline-none placeholder:text-slate-400"
      />
    </div>
  )
}
